import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "../data/mock";

// ------------------------------------------------------------------
// Seo: manages <title>, meta description, robots, canonical, Open
// Graph / Twitter cards and JSON-LD structured data for every page.
// ------------------------------------------------------------------

const baseBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["HealthClub", "SportsActivityLocation"],
  "@id": `${SITE.url}/#business`,
  name: SITE.name,
  alternateName: ["Iron Grit Boston", "CrossFit Boston Iron & Grit"],
  description:
    "Boston Iron Grit is a coached strength & conditioning gym in West Roxbury, Boston. Programs include CrossFit classes, the Westie Barbell Club (Olympic lifting and powerlifting), B.I.G. Engine conditioning, beginner fundamentals, HYROX training, open gym, personal training and the Live B.I.G. 365 wellness & longevity program with labs, body composition testing, peptides and GLP-1s through licensed providers.",
  url: SITE.url,
  telephone: SITE.phone,
  email: SITE.email,
  foundingDate: SITE.founded,
  priceRange: "$$",
  image: `${SITE.url}/og-image.jpg`,
  logo: `${SITE.url}/logo.png`,
  parentOrganization: { "@id": `${SITE.url}/#organization` },
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.state,
    postalCode: SITE.address.zip,
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: SITE.geo.lat, longitude: SITE.geo.lng },
  hasMap: SITE.mapsUrl,
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "05:15", closes: "19:30" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "06:30", closes: "12:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "08:00", closes: "12:00" },
  ],
  sameAs: [SITE.social.instagram, SITE.social.facebook, SITE.social.crossfit],
  areaServed: SITE.serviceAreas.map((a) => ({ "@type": "Place", name: `${a}, MA` })),
  amenityFeature: [
    { "@type": "LocationFeatureSpecification", name: "Free Parking", value: true },
    { "@type": "LocationFeatureSpecification", name: "Showers", value: true },
    { "@type": "LocationFeatureSpecification", name: "Open Gym Access", value: true },
  ],
});

const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE.url}/#organization`,
  name: SITE.name,
  alternateName: ["Iron Grit Boston", "CrossFit Boston Iron & Grit"],
  url: SITE.url,
  logo: { "@type": "ImageObject", url: `${SITE.url}/logo.png`, width: 512, height: 512 },
  foundingDate: SITE.founded,
  sameAs: [SITE.social.instagram, SITE.social.facebook, SITE.social.crossfit],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-617-637-8900",
    contactType: "customer service",
    email: SITE.email,
    areaServed: "US",
    availableLanguage: "English",
  },
});

const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  name: SITE.name,
  url: SITE.url,
  publisher: { "@id": `${SITE.url}/#organization` },
});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE.url}${c.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceSchema = (cls) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE.url}/classes/${cls.slug}#service`,
  name: `${cls.name} — ${SITE.name}`,
  serviceType: cls.name,
  description: cls.answer,
  provider: { "@id": `${SITE.url}/#business` },
  areaServed: { "@type": "Place", name: "West Roxbury, Boston, MA" },
  url: `${SITE.url}/classes/${cls.slug}`,
  ...(cls.offerPrice
    ? {
        offers: {
          "@type": "Offer",
          price: cls.offerPrice,
          priceCurrency: "USD",
          url: `${SITE.url}/pricing`,
          availability: "https://schema.org/InStock",
        },
      }
    : {}),
});

export const articleSchema = (a) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: a.title,
  description: a.metaDescription,
  image: a.image,
  datePublished: a.date,
  dateModified: a.updated,
  author: { "@type": "Person", name: a.author, worksFor: { "@id": `${SITE.url}/#business` } },
  publisher: { "@id": `${SITE.url}/#organization` },
  mainEntityOfPage: `${SITE.url}/blog/${a.slug}`,
});

export const speakableSchema = (path) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE.url}${path}#webpage`,
  url: `${SITE.url}${path}`,
  isPartOf: { "@id": `${SITE.url}/#website` },
  speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]"] },
});

const setNamedMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setPropMeta = (prop, content) => {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

// Live AggregateRating for LocalBusiness schema (fetched once per session from GBP via backend)
let _aggRating = null;
let _aggPromise = null;
function fetchAggregateRating() {
  if (!_aggPromise) {
    _aggPromise = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.rating && d.count) {
          _aggRating = { "@type": "AggregateRating", ratingValue: d.rating, reviewCount: d.count, bestRating: 5, worstRating: 1 };
        }
        return _aggRating;
      })
      .catch(() => null);
  }
  return _aggPromise;
}

export default function Seo({ title, description, path = "", schemas = [], noindex = false, ogType = "website", ogImage }) {
  const location = useLocation();
  const schemaJson = JSON.stringify(schemas);
  useEffect(() => {
    document.title = title;
    const canonicalUrl = `${SITE.url}${path || location.pathname}`;
    const img = ogImage || `${SITE.url}/og-image.jpg`;

    setNamedMeta("description", description);
    setNamedMeta("robots", noindex ? "noindex, follow" : "index, follow");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    setPropMeta("og:type", ogType);
    setPropMeta("og:site_name", SITE.name);
    setPropMeta("og:title", title);
    setPropMeta("og:description", description);
    setPropMeta("og:url", canonicalUrl);
    setPropMeta("og:image", img);
    setPropMeta("og:image:width", "1200");
    setPropMeta("og:image:height", "630");
    setNamedMeta("twitter:card", "summary_large_image");
    setNamedMeta("twitter:title", title);
    setNamedMeta("twitter:description", description);
    setNamedMeta("twitter:image", img);

    // Clean previous JSON-LD blocks that we injected
    let cancelled = false;
    const inject = (agg) => {
      document.querySelectorAll('script[data-seo="jsonld"]').forEach((n) => n.remove());
      const biz = baseBusinessSchema();
      if (agg) biz.aggregateRating = agg;
      const allSchemas = [organizationSchema(), biz, websiteSchema(), ...schemas];
      allSchemas.forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "jsonld");
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    };
    inject(_aggRating);
    if (!_aggRating) {
      fetchAggregateRating().then((agg) => {
        if (!cancelled && agg) inject(agg);
      });
    }

    return () => {
      cancelled = true;
      document.querySelectorAll('script[data-seo="jsonld"]').forEach((n) => n.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, noindex, ogType, ogImage, location.pathname, schemaJson]);

  return null;
}
